import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Topbar from "../../components/Topbar/Topbar";
import { useTranslation } from 'react-i18next';
import { FaUsers, FaFileInvoiceDollar, FaCreditCard, FaCheck, FaCalendarAlt, FaArrowRight } from 'react-icons/fa';

import "./reserve.css";
import Demande from "../../assets/Forms/Demande/Demande";
import Devis from "../../assets/Forms/Devis/Devis";
import TripCheckout from "../TripCheckout/TripCheckout";
import API from "../../API";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const parseJSON = (data) => {
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch { return []; }
  }
  return [];
};

export default function Reserve() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [bebe, setBebe] = useState(0);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [total, setTotal] = useState(0);
  const [places, setPlaces] = useState([]);
  const [voyage_id, setVoyageId] = useState('');
  const [voya, setVoya] = useState();

  const code = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Auth gate — unauthenticated users go to login and land on the voyage page afterwards
  useEffect(() => {
    if (!localStorage.getItem('protoken')) {
      const backTo = `/voyage-org/${code.code}#tarifs`;
      const next = encodeURIComponent(backTo);
      window.location.href = `/login?next=${next}`;
    }
  }, [code.code]);

  // "Retour" on step 1 sends the user back to the voyage detail page
  const goBackToVoyage = () => {
    navigate(`/voyage-org/${code.code}#tarifs`);
  };

  const queryParams = new URLSearchParams(location.search);
  const initialDepart = queryParams.get("depart");
  const initialArrivage = queryParams.get("arrivage");

  const [selectedDepart, setSelectedDepart] = useState(initialDepart);
  const [selectedArrivage, setSelectedArrivage] = useState(initialArrivage);

  const departures = voya ? parseJSON(voya.departures) : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await API.get(
          `/voyage-organize/verify/${code.code}?depart=${selectedDepart}&arrivage=${selectedArrivage}`
        );
        setVoya(response.data.voyage);
        setVoyageId(response.data.voyage.id);
        const fetchedPlaces = response.data.voyage.places;
        const filteredPlaces = fetchedPlaces.filter(
          (place) => place.max_adults >= adults && place.max_children >= children + bebe
        );
        setPlaces(filteredPlaces);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [adults, children, code.code, selectedDepart, selectedArrivage, bebe]);

  const handleDateSelect = (dep) => {
    setSelectedDepart(dep.start);
    setSelectedArrivage(dep.end);
  };

  const handleNext = () => setStep((prevStep) => Math.min(prevStep + 1, 3));
  const handleBack = () => {
    // Step 1 can't "go back" inside the wizard → leave to the voyage detail page
    if (step <= 1) {
      goBackToVoyage();
      return;
    }
    setStep((prevStep) => Math.max(prevStep - 1, 1));
  };

  // Compute max capacity from the trip's places (so users know the limits up-front)
  const maxAdults = voya?.places?.length
    ? Math.max(...voya.places.map((p) => Number(p.max_adults) || 0))
    : 9;
  const maxChildren = voya?.places?.length
    ? Math.max(...voya.places.map((p) => Number(p.max_children) || 0))
    : 9;

  const handlePlaceSelect = (place) => {
    setSelectedPlace(place);
    handleNext();
  };

  const steps = [
    { label: t("reserve.step1"), icon: <FaUsers /> },
    { label: t("reserve.step2"), icon: <FaFileInvoiceDollar /> },
    { label: t("reserve.step3"), icon: <FaCreditCard /> },
  ];

  return (
    <div className="page-reservation">
      <Topbar />
      <Navbar />

      <div className="rsv-content">
        {/* Trip Date Banner */}
        {selectedDepart && selectedArrivage && (
          <div className="rsv-trip-banner">
            <FaCalendarAlt className="rsv-trip-banner-icon" />
            <div className="rsv-trip-banner-dates">
              <span className="rsv-trip-banner-label">{t("reserve.your_trip")}</span>
              <div className="rsv-trip-banner-range">
                <span>{new Date(selectedDepart).toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                <FaArrowRight className="rsv-trip-banner-arrow" />
                <span>{new Date(selectedArrivage).toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
            </div>
            {voya?.title && <span className="rsv-trip-banner-title">{voya.title}</span>}
          </div>
        )}

        {/* Step Indicator */}
        <div className="rsv-stepper">
          {steps.map((s, i) => {
            const stepNum = i + 1;
            const isActive = step === stepNum;
            const isCompleted = step > stepNum;
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className={`rsv-stepper-line ${isCompleted ? "completed" : ""}`} />
                )}
                <div className={`rsv-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}>
                  <div className="rsv-step-circle">
                    {isCompleted ? <FaCheck /> : s.icon}
                  </div>
                  <span className="rsv-step-label">{s.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="rsv-step-content">
          {step === 1 && (
            <Demande
              onNext={() => handleNext()}
              onBack={handleBack}
              setAdults={setAdults}
              setChildren={setChildren}
              setBebe={setBebe}
              maxAdults={maxAdults}
              maxChildren={maxChildren}
              departures={departures}
              selectedDepart={selectedDepart}
              selectedArrivage={selectedArrivage}
              onDateSelect={handleDateSelect}
            />
          )}
          {step === 2 && (
            <Devis
              places={places}
              adults={adults}
              children={children}
              bebe={bebe}
              onPlaceSelect={handlePlaceSelect}
              setSelectedPlace={setSelectedPlace}
              setTotal={setTotal}
              total={total}
              selectedPlace={selectedPlace}
              onBack={handleBack}
            />
          )}
          {step >= 3 && (
            <TripCheckout
              place={selectedPlace}
              adult={adults}
              children={children}
              bebe={bebe}
              total={total}
              voyage_id={voyage_id}
              onBack={handleBack}
              onComplete={() => setStep(4)}
              voya={voya}
              depart={selectedDepart}
              arrivage={selectedArrivage}
            />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
